/**
 * Add disable ability to a model
 */
/* eslint-disable */
import { DISABLED, SEATRADIUS, SEATSTATUS } from '@/constants';
import store from '@/store/store';
import SVG from 'svg.js';
import eventbus from 'tiny-emitter/instance';
import Application from '../application';

export interface GroupableAndDisabledRequirements {
  attributes: Record<string, any>;
  view: SVG.G;
  hideLabel(): void;
  showLabel(): void;
  locked: boolean;
  shape: SVG.Shape;
  app: Application;
}

export const GroupableAndDisabledMixin = <T extends { new (...args: any[]): GroupableAndDisabledRequirements }>(
  base: T
) => {
  return class GroupableAndDisabledMixin extends base {
    targetingWrapper: any;
    leavingWrapper: any;

    get groupId() {
      return this.attributes._groupId;
    }

    set groupId(value: number | undefined) {
      if (value === undefined) {
        this.removeGroupClass();
        this.attributes._groupId = undefined;
        this.removeHoveringEventListeners();
      } else {
        if (this.attributes.status === SEATSTATUS.DISABLED) {
          this.attributes.status = SEATSTATUS.FREE;
          this.toggleDisabledVisibility();
        }
        this.attributes._groupId = value;
        this.toggleGroupClass();
        this.addHoveringEventListeners();
      }
    }

    get grouped() {
      return !!this.attributes._groupId;
    }

    initGroupedShape() {
      this.groupId = this.attributes._groupId;
    }

    toggleGroupClass() {
      if (this.grouped) {
        this.addGroupClass();
      } else {
        this.removeGroupClass();
      }
    }

    addGroupClass() {
      this.view.addClass('grouped');
    }

    removeGroupClass() {
      this.view.removeClass('grouped');
    }

    placeToGroup(groupId: number) {
      if (this.locked) {
        this.locked = false;
        eventbus.emit('seats:lock:updated');
      }
      this.removeFromPreviousGroup();
      this.groupId = groupId;
    }

    removeFromPreviousGroup() {
      if (this.groupId !== undefined) {
        this.groupId = undefined;
      }
    }

    groupTargetingEventEmitter() {
      let wrapperThis = this;
      if (!this.targetingWrapper) {
        this.targetingWrapper = function () {
          eventbus.emit('group:mouseEnter', wrapperThis);
        };
      }

      return this.targetingWrapper;
    }

    groupLeavingEventEmitter() {
      let wrapperThis = this;
      if (!this.leavingWrapper) {
        this.leavingWrapper = function () {
          eventbus.emit('group:mouseLeave', wrapperThis);
        };
      }

      return this.leavingWrapper;
    }

    addHoveringEventListeners() {
      this.view.node.addEventListener('mouseenter', this.groupTargetingEventEmitter());
      this.view.node.addEventListener('mouseleave', this.groupLeavingEventEmitter());
    }

    removeHoveringEventListeners() {
      this.view.node.removeEventListener('mouseenter', this.groupTargetingEventEmitter());
      this.targetingWrapper = undefined;
      this.view.node.removeEventListener('mouseleave', this.groupLeavingEventEmitter());
      this.leavingWrapper = undefined;
    }

    // disabled mixin
    disabledShape: SVG.Path | null = null;

    get disabled() {
      return this.attributes.status === SEATSTATUS.DISABLED;
    }

    set disabled(value) {
      if (value) {
        this.removeFromPreviousGroup();
        this.attributes.status = SEATSTATUS.DISABLED;
      } else {
        this.attributes.status = SEATSTATUS.FREE;
      }
      this.toggleDisabledVisibility();
    }

    hideDisabled() {
      this.disabled && this.disabledShape!.hide();
    }

    showDisabled() {
      this.disabled && this.disabledShape!.show();
    }

    toggleDisabledVisibility() {
      if (this.disabled) {
        this.hideLabel();
        this.addDisabledClass();
        this.initDisabledShape();
      } else {
        if (store.state.seatmap.showLabels) {
          this.showLabel();
        }
        this.removeDisabledClass();
        this.removeDisabledShape();
      }
    }

    initDisabledShape([cx, cy]: number[] = this.attributes.position) {
      (this.shape.node.parentNode as SVGElement).classList.add('disabled');
      if (!this.disabledShape || !this.disabledShape.node) {
        const size = SEATRADIUS * 1.2 * this.app.seatmap.scale;
        this.disabledShape = this.view
          .path(DISABLED.SHAPE)
          .size(size)
          .center(cx, cy)
          //.stroke({ width: 2 })
          .fill(DISABLED.FILL);
      }
      this.app.seatmap.groupIdCounter++;
    }

    removeDisabledShape() {
      this.disabledShape && this.disabledShape.remove();
      this.disabledShape = null;
    }

    addDisabledClass() {
      this.view.addClass('disabled');
    }

    removeDisabledClass() {
      this.view.removeClass('disabled');
    }

    disable() {
      if (this.locked) {
        this.locked = false;
        eventbus.emit('seats:lock:updated');
      }
      this.disabled = true;
    }

    enable() {
      if (this.locked) {
        this.locked = false;
        eventbus.emit('seats:lock:updated');
      }
      this.disabled = false;
    }

    toggleDisabled() {
      if (this.locked) {
        this.locked = false;
        eventbus.emit('seats:lock:updated');
      }
      this.disabled = !this.attributes.disabled;
    }

    setDisabled(flag: boolean) {
      if (this.locked) {
        this.locked = false;
        eventbus.emit('seats:lock:updated');
      }
      this.disabled = flag;
    }

    updateDisabledShape(x: number, y: number) {
      if (this.disabledShape) {
        const size = SEATRADIUS * 1.2 * this.app.seatmap.scale;
        this.disabledShape.size(size).center(x, y);
      }
    }
  };
};
